import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyableId } from '@/components/CopyableId';
import { Field } from '@/components/Field';
import { PageContainer } from '@/components/PageContainer';
import { SummaryCard } from '@/components/SummaryCard';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';
import { MessagingForm } from '@/features/workflows/components/MessagingForm';
import { TaskCompletionFlagsCard } from '@/features/workflows/components/TaskCompletionFlagsCard';

const RouteComponent = () => {
  const { workflowId, taskDefId } = Route.useParams();

  const { data: workflow } = useQuery(
    trpc.workflow.findOneWorkflow.queryOptions({ id: workflowId })
  );

  const handleError = (message: string) => {
    toaster.error({ title: 'Error', description: message });
  };

  if (!workflow) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" py={16} width="100%">
          <Spinner size="lg" />
        </Flex>
      </PageContainer>
    );
  }

  const taskDef = workflow.steps.flatMap((s) => s.taskDefinitions).find((t) => t.id === taskDefId);

  if (!taskDef) {
    return (
      <PageContainer>
        <Text variant="subtle">Task definition not found.</Text>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={6}>
        <Breadcrumbs
          items={[
            { label: 'Workflows', to: '/workflows' },
            { label: workflow.name, to: `/workflows/${workflowId}` },
            { label: taskDef.displayLabel ?? taskDef.key },
          ]}
        />

        <Flex direction="column" gap={1}>
          <Text fontSize="2xl" fontWeight="bold">
            {taskDef.displayLabel ?? taskDef.key}
          </Text>
          {taskDef.description && <Text variant="subtle">{taskDef.description}</Text>}
        </Flex>

        <Flex gap={4} wrap="wrap">
          <SummaryCard title="TASK">
            {/* Uncoloured, the same treatment the step header gives a task type: it names
                what the task does, it is not a status. */}
            <Field label="Type">
              <Tag label={taskDef.taskType} variant="default" size="sm" />
            </Field>
            <Field label="Assignee">
              <Tag label={taskDef.assigneeType} variant="default" size="sm" />
            </Field>
            <Field label="Key" minW="240px">
              <CopyableId id={taskDef.key} label="task key" />
            </Field>
          </SummaryCard>
        </Flex>

        <TaskCompletionFlagsCard
          // Keyed so switching task definitions remounts the editor. Without it
          // React reuses the instance and the new task inherits the previous
          // one's staged flags and drafts.
          key={taskDef.id}
          workflowId={workflowId}
          taskDef={taskDef}
          warnings={workflow.completionWarnings}
        />

        <Flex direction="column" gap={3}>
          <Text fontSize="lg" fontWeight="bold">
            Messages
          </Text>
          {(taskDef.taskMessagings ?? []).length === 0 ? (
            <Text variant="subtle" fontSize="sm">
              No base messagings defined for this task.
            </Text>
          ) : (
            (taskDef.taskMessagings ?? []).map((messaging) => (
              <MessagingForm
                key={messaging.id}
                messaging={messaging}
                workflowId={workflowId}
                onError={handleError}
              />
            ))
          )}
        </Flex>
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflows/$workflowId/tasks/$taskDefId')({
  component: RouteComponent,
});
